import { GiftCardDetails } from 'app/src/features/Shopping/Direct/Giftcard/components/GiftCardDetails'
import { GiftCardLeftPanelSide } from 'app/src/features/Shopping/Direct/Giftcard/components/GiftCardLeftPanelSide'
import { GiftCardRightPanelSide } from 'app/src/features/Shopping/Direct/Giftcard/components/GiftCardRightPanelSide'
import {
  cardPollingWorker,
  CardPurchaseStatus,
  getCardPurchaseStatus,
  type CardInProgressViewBaseProps,
  type CardPurchaseData,
} from 'app/src/features/Shopping/shared/utils'
import { shoppingApi, type portalConfigurationApi } from 'app/src/services/welfareportal'
import { BackToCatalogButton, ErrorPage, Image, PortalConfirmDialog } from 'app/src/shared/components'
import { SafetyThresholdBox } from 'app/src/shared/components/SafetyThresholdBox'
import { useWebAppContext } from 'app/src/shared/hooks'
import successImage from 'app/static/images/success_img.png'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

function InProgressView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GiftCard' })
  const context = useWebAppContext()

  const shoppingConfig: Required<portalConfigurationApi.ShoppingConfig['cardPolling']> = useMemo(() => {
    const def_cardPollingInterval = 1000
    const def_cardTakeInChargeMaxCount = 10
    if (!context.portalConfiguration.shoppingConfig?.cardPolling)
      return {
        cardPollingInterval: def_cardPollingInterval,
        cardTakeInChargeMaxCount: def_cardTakeInChargeMaxCount,
      }
    else
      return {
        cardPollingInterval: context.portalConfiguration.shoppingConfig.cardPolling.cardPollingInterval ?? 1000,
        cardTakeInChargeMaxCount: context.portalConfiguration.shoppingConfig.cardPolling.cardTakeInChargeMaxCount ?? 10,
      }
  }, [context.portalConfiguration.shoppingConfig])

  useEffect(() => {
    cardPollingWorker(props.purchaseData, shoppingConfig, context).then(props.setPurchaseData)
  }, [context, props?.purchaseData, props?.setPurchaseData, shoppingConfig])

  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        <ProgressSpinner data-testid='loading-element' className='flex' />
        <div className='mt-4'>{t('requestInProgressDescription')}</div>
      </div>
    </div>
  )
}

function FailedView() {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GiftCard' })

  return (
    <ErrorPage errorMessage={t('requestInFailErrorMessage')}>
      <div className='mx-auto flex justify-center'>
        <BackToCatalogButton />
      </div>
    </ErrorPage>
  )
}

function TakeInChargeView() {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GiftCard' })

  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        <Image
          alt="Immagine che descrive la presa in carico dell'operazione"
          pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
          src={successImage}
        />
        <div className='mx-auto' dangerouslySetInnerHTML={{ __html: t('takeInChargeMessage') }} />
        <div className='mx-auto flex justify-center'>
          <BackToCatalogButton />
        </div>
      </div>
    </div>
  )
}

function CompletedView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GiftCard' })

  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        {props.purchaseData.order?.status === 'draft' && (
          <>
            <Image
              alt="Immagine di successo dell'operazione"
              pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
              src={successImage}
            />
            <div className='mx-auto' dangerouslySetInnerHTML={{ __html: t('successMessage') }} />
          </>
        )}
        <div className='mx-auto mt-4'>
          <GiftCardDetails order={props.purchaseData.order} funnelMode={props.purchaseData.order?.status === 'draft'} />
        </div>
      </div>
    </div>
  )
}

function DraftView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GiftCard' })

  const [showConfirm, setShowConfirm] = useState(false)
  const context = useWebAppContext()
  const orderStartMutation = shoppingApi.useOrderStart()
  const [showUsageModal, setShowUsageModal] = useState(false)

  const credit = shoppingApi.useCreditGet(
    {},
    {
      meta: {
        skip404Errors: true,
      },
    },
  )

  const shouldDisplayFringeWarning = useMemo(() => {
    if (context.userInfo.welfareCampaign.configuration.fringeWarningThreshold) {
      if (
        credit.data?.fringeAssigned !== undefined &&
        credit.data.creditByServiceCategory.welfareArt51.total !== undefined &&
        credit.data.creditByServiceCategory.welfareArt51.available !== undefined
      ) {
        const productFacePrice = props.purchaseData.order.variant.priceDetails?.facePrice ?? 0
        const fringeWarningThresholdRatio = context.userInfo.welfareCampaign.configuration.fringeWarningThreshold / 100
        const used =
          credit.data.creditByServiceCategory.welfareArt51.total - credit.data.creditByServiceCategory.welfareArt51.available
        const percUsed = (used + productFacePrice) / credit.data.fringeAssigned
        if (percUsed >= fringeWarningThresholdRatio) {
          return true
        }
        return false
      }
    }
    return false

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.userInfo.welfareCampaign.configuration.fringeWarningThreshold, credit.data])

  const [confirmBtnEnabled, setConfirmBtnEnabled] = useState(false)

  return (
    <>
      {showConfirm && (
        <PortalConfirmDialog
          accept={async () => {
            try {
              await orderStartMutation.mutateAsync({ pathParams: { orderId: props.purchaseData.order.orderId } })
              localStorage.setItem('fringeThresholdAccept', confirmBtnEnabled ? confirmBtnEnabled.toString() : 'false')
              props.setPurchaseData({ status: CardPurchaseStatus.INPROGRESS, order: props.purchaseData.order })
            } catch (err) {
              context.logger?.error('Error While Starting Order', { orderId: props.purchaseData.order.orderId, error: err })
              props.setPurchaseData({ status: CardPurchaseStatus.FAILED, order: props.purchaseData.order })
            } finally {
              setShowConfirm(false)
            }
          }}
          reject={() => setShowConfirm(false)}
          body={t('confirmDialog.body')}
          title={t('confirmDialog.title')}
        />
      )}

      <div className='m-4 mx-auto max-w-5xl'>
        <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <GiftCardLeftPanelSide
            order={props.purchaseData.order}
            setShowUsageModal={setShowUsageModal}
            showUsageModal={showUsageModal}
          />
          <GiftCardRightPanelSide order={props.purchaseData.order} showCredit showFringeSpinner />
        </div>
        <div className='mt-4 grid rounded-md bg-[var(--card-background)] p-4'>
          <div>
            <h2 className='font-inter text-[21px] font-bold leading-[31.5px]'>{t('detailDescription')}</h2>
          </div>
          <div className='mt-3 grid grid-cols-1 md:grid-cols-2'>
            <div className='grid-rows-2'>
              <div>{t('buyerDescription')}</div>
              <div className='mt-2'>
                <h3>
                  {props.purchaseData.order.userInfo?.firstName} {props.purchaseData.order.userInfo?.lastName}
                </h3>
              </div>
            </div>
            <div className='grid-rows-2'>
              <div>{t('usageDestinationDescription')}</div>
              <div className='mt-2'>
                <h3>{props.purchaseData.order.productAdditionalInfo?.merchandiseCategory ?? '-'}</h3>
              </div>
            </div>
          </div>
        </div>
        {
          // box fringe
          context.userInfo.welfareCampaign.configuration.fringeWarningThreshold && shouldDisplayFringeWarning ? (
            <div className='mt-4'>
              <SafetyThresholdBox
                threshold={context.userInfo.welfareCampaign.configuration.fringeWarningThreshold}
                onChange={function (value: boolean): void {
                  setConfirmBtnEnabled(value)
                }}
              />
            </div>
          ) : (
            <></>
          )
        }
      </div>
      <div className='m-6 text-center'>
        <Button
          disabled={shouldDisplayFringeWarning ? !confirmBtnEnabled : false}
          rounded
          label={t('confirmButtonLabel')}
          onClick={() => setShowConfirm(true)}
        />
      </div>
    </>
  )
}

export function GiftCardFunnel(props: { order: shoppingApi.OrderDetails }) {
  const [purchaseData, setPurchaseData] = useState<CardPurchaseData>()
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GiftCard' })

  const title = useMemo(() => {
    switch (purchaseData?.status) {
      case CardPurchaseStatus.DRAFT:
        return t('almostDoneTitle')
      case CardPurchaseStatus.INPROGRESS:
        return t('requestInProgressTitle')
      default:
        return ''
    }
  }, [purchaseData, t])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [purchaseData?.status])

  useEffect(() => {
    setPurchaseData({ order: props.order, status: getCardPurchaseStatus(props.order) })
  }, [props.order])

  return (
    <>
      <h1 className='welfareportal-titlecenter' aria-label='welfareportal-titlecenter'>
        {title}
      </h1>
      {purchaseData?.status === CardPurchaseStatus.DRAFT && (
        <DraftView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.INPROGRESS && (
        <InProgressView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.CANCELED && (
        <CompletedView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.FAILED && <FailedView />}
      {purchaseData?.status === CardPurchaseStatus.TAKEINCHARGE && <TakeInChargeView />}
      {purchaseData?.status === CardPurchaseStatus.COMPLETED && (
        <CompletedView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
    </>
  )
}
